import { 
  getAuth, 
  signInAnonymously, 
  GoogleAuthProvider, 
  signInWithPopup,
  signInWithCustomToken 
} from 'firebase/auth';
import { API_URL } from '../constants/string-constants';

export const switchToFirebaseUser = async (targetUserId) => {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    // If we're already the target user, no need to switch
    if (currentUser && currentUser.uid === targetUserId) {
      return currentUser;
    }
    
    // Sign out current user and sign in as the target user
    // For anonymous users, we'll need to sign in anonymously and hope we get the same UID
    // This is a limitation of Firebase - we can't directly switch to a specific anonymous user
    
    // Store current state before switching
    const wasSignedIn = !!currentUser;
    
    if (currentUser) {
      await auth.signOut();
    }
    
    // Sign in anonymously - Firebase will create a new anonymous user
    // We can't force it to be a specific UID, so we'll need to handle this differently
    const userCredential = await signInAnonymously(auth);
    
    // Check if we got the expected user ID
    if (userCredential.user.uid === targetUserId) {
      console.log(`Successfully switched to target Firebase user: ${targetUserId}`);
      return userCredential.user;
    } else {
      console.log(`Signed in as new anonymous user: ${userCredential.user.uid}, expected: ${targetUserId}`);
      // We got a different user ID than expected
      // This is expected behavior with Firebase anonymous auth
      return userCredential.user;
    }
    
  } catch (error) {
    console.error('Error switching Firebase user:', error);
    throw error;
  }
};

export const signInWithAccountNumber = async (accountNumber) => {
  try {
    // First, ensure we have a Firebase user
    const auth = getAuth();
    let currentUser = auth.currentUser;
    
    if (!currentUser) {
      const userCredential = await signInAnonymously(auth);
      currentUser = userCredential.user;
    }
    
    // The backend will handle the account number verification
    // We don't need to switch users since the account number becomes associated
    // with whatever Firebase user makes the request
    
    return currentUser;
  } catch (error) {
    console.error('Error signing in with account number:', error);
    throw error;
  }
};

export const signInWithGoogle = async () => {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    const provider = new GoogleAuthProvider();
    
    // Only request email scope, skip profile info
    provider.setCustomParameters({
      prompt: 'select_account'
    });
    provider.addScope('email');
    
    // Sign in with Google
    const result = await signInWithPopup(auth, provider);
    const googleUser = result.user;
    
    console.log('Google sign-in successful:', googleUser.uid);
    
    // Always try to link first (whether anonymous or not)
    // The backend will handle if this Google account already exists
    console.log('Attempting to link Google account to current user:', currentUser?.uid);
    
    // Get fresh ID token for current user
    const idToken = await currentUser.getIdToken(true);
    console.log('Got user token for Google linking');
    
    // Try to link Google account to current user
    const response = await fetch(`${API_URL}auth/google/link`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}` 
      },
      body: JSON.stringify({
        google_id: googleUser.uid,
        email: googleUser.email
      })
    });
    
    if (!response.ok) {
      throw new Error(`Google link request failed: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data.switch_required) {
      console.log('Google account already exists, switching to original user:', data.user_id);
      // This Google account belongs to a different user - switch to that user
      await signInWithCustomToken(auth, data.custom_token);
      return auth.currentUser;
    }
    
    console.log('Google account linked successfully to current user');
    return googleUser;
    
  } catch (error) {
    console.error('Google sign-in failed:', error);
    throw error;
  }
};